// The Single sign-on page's read model: what WorkOS holds for the org, and what that means for
// the admin. Pure so the copy and button set are tested without rendering.

export type SsoConnection = { id: string; name: string; type: string; state: string; updatedAt: string };
export type SsoDomain = { domain: string; state: string };
export type SsoStatus = { entitled: boolean; connection: SsoConnection | null; domains: SsoDomain[] };

export type SsoPhase = 'not-entitled' | 'not-set-up' | 'draft' | 'active';

// 'none' means no portal button at all: the org is not entitled to open it.
export type SsoView = {
  phase: SsoPhase;
  headline: string;
  detail: string;
  provider: string | null;
  primaryAction: 'setup' | 'manage' | 'none';
  canRemove: boolean;
  needsDomainVerification: boolean;
};

const LAPSED_DETAIL = 'Single sign-on is no longer enabled for your organisation. The connection can be removed but not changed; contact Cloudhouse to restore it.';

// WorkOS connection_type values the hub names for the customer. Anything else shows raw: a new
// provider must not silently render as "Unknown" when the raw type is at least searchable.
const PROVIDER_LABELS: Record<string, string> = {
  MicrosoftEntraSAML: 'Microsoft Entra ID (SAML)',
  MicrosoftEntraOIDC: 'Microsoft Entra ID (OpenID Connect)',
  AzureSAML: 'Microsoft Entra ID (SAML)',
  OktaSAML: 'Okta (SAML)',
  OktaOIDC: 'Okta (OpenID Connect)',
  GoogleSAML: 'Google Workspace (SAML)',
  GoogleOAuth: 'Google Workspace',
  OneLoginSAML: 'OneLogin (SAML)',
  PingFederateSAML: 'PingFederate (SAML)',
  JumpCloudSAML: 'JumpCloud (SAML)',
  GenericSAML: 'SAML',
  GenericOIDC: 'OpenID Connect',
};

export function describeSso(status: SsoStatus): SsoView {
  const { connection, entitled } = status;
  // Domain verification is a step inside SSO setup, not a thing to do before it exists — and the
  // portal is closed to an unentitled org, so it is never offered there either. WorkOS will not
  // activate a connection until the org has a verified domain, so an org with none must be offered it.
  const hasVerifiedDomain = status.domains.some((d) => d.state === 'verified');
  const needsDomainVerification = entitled && connection !== null
    && (status.domains.length === 0 || status.domains.some((d) => d.state !== 'verified'));

  if (!connection) {
    if (!entitled) {
      return {
        phase: 'not-entitled',
        headline: 'Single sign-on comes with a paid subscription',
        detail: 'Single sign-on is included with any paid Cloudhouse subscription and is not available during trials. '
          + 'Once your organisation is on a paid plan, this page will let you connect your identity provider.',
        provider: null,
        primaryAction: 'none',
        canRemove: false,
        needsDomainVerification: false,
      };
    }
    return {
      phase: 'not-set-up',
      headline: 'Single sign-on is not set up',
      detail: 'Connect your identity provider so your users sign in with the accounts they already have. '
        + 'The account that created this organisation keeps its password and can use single sign-on too once your domain is verified.',
      provider: null,
      primaryAction: 'setup',
      canRemove: false,
      needsDomainVerification,
    };
  }

  const provider = PROVIDER_LABELS[connection.type] ?? connection.type;
  if (connection.state === 'active') {
    return {
      phase: 'active',
      headline: 'Single sign-on is active',
      detail: entitled
        ? 'Users on a verified domain are sent to your identity provider when they sign in.'
        : LAPSED_DETAIL,
      provider,
      primaryAction: entitled ? 'manage' : 'none',
      canRemove: true,
      needsDomainVerification,
    };
  }

  // draft, requires_type, or anything WorkOS adds later: the connection exists but cannot sign anyone in.
  let draftDetail = LAPSED_DETAIL;
  if (entitled) {
    draftDetail = hasVerifiedDomain
      ? 'The connection exists but has not passed a test sign-in yet. Open the setup portal to finish it.'
      : 'Verify your email domain first. Single sign-on can only be tested and switched on for a verified domain.';
  }
  return {
    phase: 'draft',
    headline: 'Single sign-on setup is incomplete',
    detail: draftDetail,
    provider,
    primaryAction: entitled ? 'manage' : 'none',
    canRemove: true,
    needsDomainVerification,
  };
}

export function formatSsoDate(iso: string | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
